import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  LocalOfferOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";

const emptyForm = {
  id: null,
  code: "",
  description: "",
  credits: 50,
  max_redemptions: "",
  starts_at: "",
  expires_at: "",
  active: true,
};

export default function MarketingPromos({ lang }) {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [rows, setRows] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [snack, setSnack] = React.useState("");

  const load = React.useCallback(async () => {
    const res = await hitAxios({
      path: "/api/admin/marketing/promos",
      post: false,
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) setRows(res.data.data || []);
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      code: row.code || "",
      description: row.description || "",
      credits: row.credits || 0,
      max_redemptions: row.max_redemptions ?? "",
      starts_at: row.starts_at ? String(row.starts_at).slice(0, 10) : "",
      expires_at: row.expires_at ? String(row.expires_at).slice(0, 10) : "",
      active: !!row.active,
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const res = await hitAxios({
      path: "/api/admin/marketing/promos/save",
      post: true,
      admin: true,
      obj: {
        ...form,
        max_redemptions:
          form.max_redemptions === "" ? null : Number(form.max_redemptions),
      },
      showSnackbar: false,
    });
    setSaving(false);
    if (res?.data?.success) {
      setSnack(res.data.msg || "Saved");
      setOpen(false);
      load();
    } else {
      setSnack(res?.data?.msg || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    const res = await hitAxios({
      path: "/api/admin/marketing/promos/delete",
      post: true,
      admin: true,
      obj: { id },
      showSnackbar: false,
    });
    if (res?.data?.success) load();
    setSnack(res?.data?.msg || (res?.data?.success ? "Deleted" : "Delete failed"));
  };

  return (
    <Box>
      <PageHeader
        title={lang?.marketingPromos || "Promo Codes"}
        subtitle="Credit coupons for ads, partners, influencers, and win-back offers. Users redeem in Billing."
        icon={LocalOfferOutlined}
        primaryAction={
          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={openCreate}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            New promo
          </Button>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Share codes like <strong>LAUNCH100</strong> in Meta ads, partner emails, or affiliate
        posts. Each user can redeem a code once. Credits land in their wallet instantly.
      </Alert>

      {snack ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSnack("")}>
          {snack}
        </Alert>
      ) : null}

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              {["Code", "Credits", "Redeemed", "Window", "Status", ""].map((h) => (
                <TableCell key={h || "a"} sx={{ fontWeight: 700 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 3, textAlign: "center" }}
                  >
                    No promo codes yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography fontWeight={800} fontFamily="monospace">
                      {row.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.description || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>+{row.credits}</TableCell>
                  <TableCell>
                    {row.redemption_count}
                    {row.max_redemptions != null ? ` / ${row.max_redemptions}` : ""}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {row.starts_at
                        ? String(row.starts_at).slice(0, 10)
                        : "Anytime"}{" "}
                      →{" "}
                      {row.expires_at
                        ? String(row.expires_at).slice(0, 10)
                        : "No end"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.active ? "active" : "paused"}
                      color={row.active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(row.id)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? "Edit promo" : "New promo"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Code"
              size="small"
              fullWidth
              value={form.code}
              onChange={(e) =>
                setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))
              }
              helperText="Letters, numbers, _ and -"
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
            <TextField
              label="Credits to grant"
              type="number"
              size="small"
              fullWidth
              value={form.credits}
              onChange={(e) => setForm((p) => ({ ...p, credits: Number(e.target.value) }))}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Max redemptions (optional)"
              type="number"
              size="small"
              fullWidth
              value={form.max_redemptions}
              onChange={(e) =>
                setForm((p) => ({ ...p, max_redemptions: e.target.value }))
              }
              helperText="Leave blank for unlimited"
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="Starts"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.starts_at}
                onChange={(e) => setForm((p) => ({ ...p, starts_at: e.target.value }))}
              />
              <TextField
                label="Expires"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.expires_at}
                onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.active}
                  onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                />
              }
              label={form.active ? "Active" : "Paused"}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <LoadingButton loading={saving} variant="contained" onClick={save}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
